import {IApiBusinessProfileProto, IApiBusinessProto, space} from "../../provider_api";
import ProviderApprovalState = space.kenko.proto.ProviderApprovalState;
import BusinessSubscriptionType = space.kenko.proto.BusinessSubscriptionType;
import {getSubscriptionType} from "../../util/SubscriptionUtil";
import React, {ReactElement, ReactNode} from "react";
import {getBookingHost} from "../../util/HostUtils";


export type ProfileValidation = {
  title: string,
  description: (business: IApiBusinessProto | null | undefined) => ReactNode,
  link: string,

  // This step is marked as completed if there are no issues..
  issues: (business: IApiBusinessProto | null | undefined, profile: IApiBusinessProfileProto | null | undefined) => string[],
  isRequired: (business: IApiBusinessProto | null | undefined) => boolean,
}

export const profileValidations: ProfileValidation[] = [
  {
    title: 'Complete Your Business Profile',
    description: () => 'Your practice begins with your story. Share the details that bring it to life—your name, location, description, and specialties. This is where clients begin to understand not just what you do, but who you are.',
    link: '/home/profile',
    issues: (business, profile) => {
      const issues = [];

      if (!profile?.proto?.businessDescription || !profile?.proto?.servicesOffered) {
        issues.push('Please complete your business profile')
      }

      if (business?.businessSettings?.offersInPersonServices && 
          !profile?.locations?.filter(l => !l.archived).length) {
        issues.push('Please add at least one location');
      }

      return issues;
    },
    isRequired: () => true,
  },

  {
    title: 'Add Your Team',
    description: () => 'Each practitioner adds a new dimension to your practice. Introduce your team members, their areas of expertise, and where they practice—so clients can find the right connection and care.',
    link: '/home/team',
    issues: (business, profile) => {
      const issues = [];
      const numPractitioners = profile?.practitioners?.filter(p => !p.archived).length
      if (numPractitioners == 0) {
        issues.push('Please add at least one practitioner to your team.');
      }
      return issues;
    },
    isRequired: business => getSubscriptionType(business) == BusinessSubscriptionType.LISTING_PLUS_BOOKING
  },

  {
    title: 'List Your Services and/or Classes',
    description: () => 'You’ve outlined your services—now let clients know when you’re available. Add one-on-one sessions, classes, or workshops so people can easily find time with you.',
    link: '/home/catalog/services',
    issues: (business, profile) => {
      const issues = [];

      if (business?.businessSettings?.offersAppointments) {
        if (!profile?.practice?.services?.filter(s => !s.archived)?.length) {
          issues.push('Please add at least one service to your Catalog.');
        }
      }

      if (business?.businessSettings?.offersClasses) {
        if (!profile?.studio?.classTemplates?.filter(c => !c.archived)?.length) {
          issues.push('Please add at least one class to your Catalog.');
        }
      }

      return issues;
    },
    isRequired: business => getSubscriptionType(business) == BusinessSubscriptionType.LISTING_PLUS_BOOKING
  },

  {
    title: 'Update your calendar',
    description: () => 'Your calendar is your command center. Add offerings, update your schedule, and refine as you grow. Hosting classes or workshops? Include specific teachers, times, and locations. You can always come back to adjust or expand.',
    link: '/home/calendar',
    issues: (business, profile) => {
      if (business?.onboardingState?.createdFirstCalendarAppt) {
        return [];
      }

      return ['Please create at least one class or availability block'];
    },
    isRequired: business => getSubscriptionType(business) == BusinessSubscriptionType.LISTING_PLUS_BOOKING
  },

  {
    title: 'Set Up Payment Information',
    description: () => <>Activate your account by connecting your payment details.<br/><br/>
      Under the Billing tab, set up Stripe integration to securely receive payments and manage your earnings with
      ease.</>,
    link: '/home/billing',
    issues: (business, profile) => {
      if (!business?.connectedAccountStatus?.accountIsReady) {
        return ['Please connect your bank account to receive payments from clients.'];
      }

      return [];
    },
    isRequired: business => getSubscriptionType(business) == BusinessSubscriptionType.LISTING_PLUS_BOOKING
  },

  {
    title: 'Activate Your Membership',
    description: () => <>
      Activate your Kensu membership through Stripe, our secure payment platform.
    </>,
    link: '/home/settings/membership',
    issues: (business, profile) => {
      if (business?.subscription?.isActive) {
        return [];
      }

      return ['Activate your Kensu membership.'];
    },
    isRequired: () => true,
  },

  {
    title: 'Publish on Kensu',
    description: () => <>
      When everything feels aligned, publish your site and welcome your first bookings.
    </>,
    link: '/home/publishing',
    issues: (business, profile) => {
      if (!business?.published) {
        return ['Publish your site on Kensu'];
      }

      return [];
    },
    isRequired: () => true,
  },
];

// This is done for onboarding.
export const profileIsReadyToPublish = (
    business: IApiBusinessProto | null | undefined,
    profile: IApiBusinessProfileProto | undefined | null) => {

  return onboardingStepsRequired(business, profile) - onboardingStepsCompleted(business, profile) == 1;
}

export const getNextOnboardingStep = (
    business: IApiBusinessProto | null | undefined,
    profile: IApiBusinessProfileProto | undefined | null): {stepNumber: number, step: ProfileValidation | null} => {

  for (let i = 0; i < profileValidations.length; i++) {
    if (profileValidations[i].isRequired(business) &&
        profileValidations[i].issues(business, profile).length > 0) {
      return {stepNumber: i, step: profileValidations[i]};
    }
  }

  return {stepNumber: -1, step: null};
}

export const onboardingStepsCompleted = (
    business: IApiBusinessProto | null | undefined,
    profile: IApiBusinessProfileProto | undefined | null) => {

  return profileValidations.filter(v => v.isRequired(business) && v.issues(business, profile).length == 0).length;
}

export const onboardingStepsRequired = (
    business: IApiBusinessProto | null | undefined,
    profile: IApiBusinessProfileProto | undefined | null) => {

  return profileValidations.filter(v => v.isRequired(business)).length;
}